import type { SalesChecklistSection } from "@expert/contentful";

import { Accordion, Group, Stack, Text } from "@mantine/core";
import { type JSX, type ReactNode, useEffect } from "react";

import { useSalesContext } from "../../../../../sales-context/SalesContext";
import {
    type OfferChecklistKey,
    type OfferChecklistState,
    useOfferChecklistStore,
} from "../../../../../stores/useOfferChecklistStore";
import { isSectionComplete } from "../utils/isSectionComplete";
import { CoverageCarousel } from "./carousels/CoverageCarousel";
import { EngagementCarousel } from "./carousels/EngagementCarousel";
import { RapportCarousel } from "./carousels/RapportCarousel";
import { ChecklistItem } from "./ChecklistItem";
import { CompletionBadge } from "./CompletionBadge";

export function ChecklistSection({
    section,
    onSectionComplete,
    prependSlot,
    hiddenItemKeys,
}: {
    section: SalesChecklistSection;
    onSectionComplete: (title: string) => void;
    prependSlot?: ReactNode;
    hiddenItemKeys?: string[];
}): JSX.Element | null {
    const { sessionId, logger } = useSalesContext();
    const data = useOfferChecklistStore((state) => state.getData(sessionId));
    const visibleItems = hiddenItemKeys?.length
        ? section.checklistItems.filter((item) => !hiddenItemKeys.includes(item.key))
        : section.checklistItems;
    const isCompleted = isSectionComplete(visibleItems, data);

    useEffect(() => {
        if (!isCompleted) return;
        onSectionComplete(section.title);
    }, [isCompleted, onSectionComplete, section.title]);

    return (
        <>
            <Accordion.Item key={section.title} value={section.title}>
                <Accordion.Control styles={{ control: { opacity: 1 } }}>
                    <Group justify="space-between" pr={"sm"}>
                        <Text size="xs" c="dark.1" fw="var(--mantine-font-weight-heavy)">
                            {section.title}
                        </Text>
                        <CompletionBadge
                            totalSteps={visibleItems.length}
                            completedSteps={getCompletedSteps(visibleItems, data)}
                        />
                    </Group>
                </Accordion.Control>
                <Accordion.Panel>
                    <Stack gap="xs">
                        {prependSlot}
                        {visibleItems.map((item) => {
                            const renderType = item.additionalRenderType ?? item.key;
                            const trackedKeys = [
                                "RAPPORT",
                                "COVERAGE",
                                "DISCOVERY_QUESTION",
                                "ENGAGEMENT_QUESTION",
                            ] as const;
                            // Remove this once all Contentful Entries are migrated to use additionalRenderType
                            if (
                                trackedKeys.includes(item.key as (typeof trackedKeys)[number]) &&
                                item.additionalRenderType !== item.key
                            ) {
                                logger.debug(
                                    {
                                        key: item.key,
                                        additionalRenderType: item.additionalRenderType,
                                    },
                                    "[ChecklistSection] item.key has no matching additionalRenderType",
                                );
                            }
                            return (
                                <Stack gap={4} key={item.phrase}>
                                    <ChecklistItem item={item} />
                                    {renderType === "RAPPORT" && (
                                        <RapportCarousel defaultExample={item.defaultExample} />
                                    )}
                                    {renderType === "COVERAGE" && (
                                        <CoverageCarousel
                                            defaultExample={item.defaultExample}
                                            staticExamples={item.examples}
                                        />
                                    )}
                                    {renderType != null &&
                                        ["DISCOVERY_QUESTION", "ENGAGEMENT_QUESTION"].includes(renderType) && (
                                            <EngagementCarousel examples={item.examples ?? []} />
                                        )}
                                </Stack>
                            );
                        })}
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>
        </>
    );
}

const getCompletedSteps = (items: SalesChecklistSection["checklistItems"], data: OfferChecklistState["data"]) => {
    return items.filter((item) => data[item.key as OfferChecklistKey]).length;
};
